import { isLiveEvent, type LiveEventSink, type LiveEventSource, type LiveSourceStatus } from "./protocol";

export class GatewayLiveSource implements LiveEventSource {
  readonly sourceId = "gateway-websocket";
  #socket: WebSocket | null = null;
  #reconnectTimer = 0;
  #url: string | null = null;
  #stopped = true;

  constructor(
    private readonly hub: LiveEventSink,
    private readonly onStatus: (status: LiveSourceStatus) => void,
  ) {}

  connect(url: string): void {
    this.#url = url;
    this.#stopped = false;
    this.#open();
  }

  start(): void {
    if (!this.#url) return;
    this.#stopped = false;
    this.#open();
  }

  stop(): void {
    this.#stopped = true;
    window.clearTimeout(this.#reconnectTimer);
    const socket = this.#socket;
    this.#socket = null;
    socket?.close();
    this.onStatus("offline");
  }

  disconnect(): void {
    this.stop();
  }

  #open(): void {
    if (this.#stopped || !this.#url) return;
    window.clearTimeout(this.#reconnectTimer);
    const previous = this.#socket;
    this.#socket = null;
    previous?.close();
    this.onStatus("connecting");
    try {
      const socket = new WebSocket(this.#url);
      this.#socket = socket;
      socket.addEventListener("open", () => this.onStatus("connected"));
      socket.addEventListener("message", (message) => {
        try {
          const payload: unknown = JSON.parse(String(message.data));
          if (isLiveEvent(payload)) this.hub.publish(payload);
        } catch {
          // Ignore malformed frames. The production gateway records these.
        }
      });
      socket.addEventListener("close", () => {
        if (this.#socket !== socket) return;
        this.#socket = null;
        this.onStatus("offline");
        if (!this.#stopped) this.#reconnectTimer = window.setTimeout(() => this.#open(), 2_000);
      });
      socket.addEventListener("error", () => this.onStatus("offline"));
    } catch {
      this.onStatus("offline");
      if (!this.#stopped) this.#reconnectTimer = window.setTimeout(() => this.#open(), 2_000);
    }
  }
}
